import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  input,
  OnInit,
  output,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { HttpErrorResponse } from '@angular/common/http';
import {
  BdCompanyContextService,
  BdShareablesService,
} from '@bindoc-projects/bindoc-permissions-lib/data';
import {
  BdCompany,
  BdCompanyGroup,
  ShareableFolderObj,
} from '@bindoc-projects/resource-domain-lib';
import { TreeNode } from 'primeng/api';
import { IconField } from 'primeng/iconfield';
import { InputIcon } from 'primeng/inputicon';
import { InputText } from 'primeng/inputtext';
import { SkeletonModule } from 'primeng/skeleton';
import { TooltipModule } from 'primeng/tooltip';
import {
  TreeModule,
  TreeNodeExpandEvent,
  TreeNodeSelectEvent,
} from 'primeng/tree';
import { EMPTY, forkJoin, Observable, of, Subject } from 'rxjs';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  expand,
  map,
  reduce,
  switchMap,
  take,
} from 'rxjs/operators';

/** Discriminates the kind of a move-form tree node (stored on `node.data`). */
type MoveNodeKind =
  'user-root' | 'company' | 'group' | 'folder' | 'more-companies' | 'skeleton';

interface MoveNodeData {
  kind: MoveNodeKind;
  /** Company id — only for `company` nodes (whose groups load on expand). */
  companyId?: string;
  /**
   * True for a `group` node whose root folder could not be resolved (e.g. the
   * user is not a member → 403). Such a node is shown for context but is not a
   * valid move destination, so it is rendered locked and non-selectable.
   */
  forbidden?: boolean;
  /** Hover explanation, shown when the node is not selectable. */
  tooltip?: string;
}

/**
 * Move-destination picker. Follows the global company context: with a company
 * selected in the header only that company (and its groups) is offered as a
 * destination — matching the shareables scope sidebar — while "Alle Kunden"
 * lists every company the user can reach.
 *
 * Mirrors the scope-sidebar's loading/filter model:
 * the search field lives outside the `p-tree` (so it stays mounted and focused
 * while filtering) and is debounced; the initial/filter load swaps the tree for
 * a skeleton row list, while paging in more companies splices skeleton rows into
 * the tree. Company search is the only server-backed filter — the user's own
 * resources are shown as a fixed root when not searching.
 */
@Component({
  selector: 'bd-move-form',
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="flex flex-col">
      @if (!pinnedCompany()) {
        <div class="pb-2">
          <p-iconfield iconPosition="left" class="w-full">
            <p-inputicon>
              <i class="pi pi-search"></i>
            </p-inputicon>
            <input
              pInputText
              type="text"
              fluid
              (input)="onSearch($event)"
              placeholder="Kunde suchen"
              aria-label="Kunde suchen"
            />
          </p-iconfield>
        </div>
      }

      @if (loading()) {
        <div
          class="flex flex-col px-3 pt-1"
          role="status"
          aria-label="Kunden werden geladen"
        >
          @for (width of skeletonRows; track $index) {
            <div class="flex items-center gap-2 py-2">
              <p-skeleton shape="circle" size="1rem" />
              <p-skeleton height="0.85rem" [width]="width" />
            </div>
          }
        </div>
      } @else {
        <p-tree
          [value]="displayNodes()"
          selectionMode="single"
          [(selection)]="selectedNode"
          scrollHeight="24rem"
          (onNodeExpand)="onExpand($event)"
          (onNodeSelect)="onSelect($event)"
        >
          <ng-template #node let-node>
            @if (node.type === 'skeleton') {
              <div class="flex items-center gap-2 w-full">
                <p-skeleton shape="circle" size="1rem" />
                <span class="flex-1 min-w-0">
                  <p-skeleton height="0.85rem" />
                </span>
              </div>
            } @else {
              <span
                [pTooltip]="$safeNavigationMigration(node.data?.tooltip)"
                tooltipPosition="top"
                [tooltipDisabled]="!node.data?.tooltip"
              >
                {{ node.label }}
                @if (node.data?.forbidden) {
                  <i class="pi pi-lock text-muted-color ml-2 text-sm"></i>
                }
              </span>
            }
          </ng-template>
        </p-tree>
      }
    </div>
  `,
  styles: [
    `
      :host {
        display: block;
      }
      /* Let the load-more skeleton bar span the full row width. */
      :host ::ng-deep .bd-move-form__skeleton-node .p-tree-node-label {
        flex: 1 1 auto;
        min-width: 0;
      }
    `,
  ],
  imports: [
    TreeModule,
    SkeletonModule,
    TooltipModule,
    IconField,
    InputIcon,
    InputText,
  ],
})
export class BdMoveFormComponent implements OnInit {
  private static readonly COMPANY_PAGE_SIZE = 25;
  private static readonly GROUP_PAGE_SIZE = 100;
  private static readonly MORE_COMPANIES_KEY = '__more-companies__';
  /** Number of skeleton placeholder rows shown while paging in more companies. */
  private static readonly MORE_SKELETON_ROWS = 3;

  public readonly folderChange = output<string | null>();

  /**
   * Whether to offer the acting user's own root ("Eigene Ressourcen") as a
   * destination.
   *
   * Defaults to `true`, which is right when someone is moving their own
   * resources. Administration screens act **on behalf of another user**, and
   * `rootFolder()` resolves to whoever is signed in — so leaving this on there
   * would let an admin move somebody else's resource into their own personal
   * space. Those screens pass `false`.
   */
  public readonly includeOwnResources = input(true);

  /**
   * The company the tree is pinned to, i.e. the active global company context
   * (`undefined` for "Alle Kunden"). While pinned there is exactly one company
   * node, no paging and no company search.
   */
  public readonly pinnedCompany = signal<BdCompany | undefined>(undefined);

  public readonly treeNodes = signal<TreeNode[]>([]);
  /** Initial + filter reload — swaps the tree for the skeleton row list. */
  public readonly loading = signal<boolean>(true);
  /** Pagination ("Weitere Kunden laden") — keeps the tree, splices skeletons. */
  public readonly loadingMore = signal<boolean>(false);
  public selectedNode: TreeNode | null = null;

  /** Varied bar widths so the initial-load skeleton reads as a natural list. */
  public readonly skeletonRows = [
    '65%',
    '80%',
    '55%',
    '78%',
    '85%',
    '60%',
    '72%',
    '50%',
  ];

  /**
   * Tree value with, while paging, skeleton placeholder rows spliced in where
   * the next companies will land — after the loaded companies, in place of the
   * "load more" row.
   */
  public readonly displayNodes = computed<TreeNode[]>(() => {
    const nodes = this.treeNodes();
    if (!this.loadingMore()) {
      return nodes;
    }
    const withoutMore = nodes.filter(
      (n) => (n.data as MoveNodeData).kind !== 'more-companies',
    );
    const skeletons = Array.from(
      { length: BdMoveFormComponent.MORE_SKELETON_ROWS },
      (_, index) => this._skeletonNode(index),
    );
    return [...withoutMore, ...skeletons];
  });

  private readonly _resourcesService = inject(BdShareablesService);
  private readonly _companyContext = inject(BdCompanyContextService);
  private readonly _destroyRef = inject(DestroyRef);
  private readonly _search$ = new Subject<string>();

  /** The user's own root folder node, shown first when not searching. */
  private _userNode: TreeNode | null = null;
  private _companyPage = 0;
  private _query = '';
  private _companiesLast = true;

  constructor() {
    // Debounce keystrokes so the server isn't hit (and the skeleton doesn't
    // flash) on every character.
    this._search$
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        takeUntilDestroyed(this._destroyRef),
      )
      .subscribe((query) => this.applyFilter(query));
  }

  ngOnInit(): void {
    this.loading.set(true);
    const pinned = this._readCompanyContext();
    this.pinnedCompany.set(pinned);
    forkJoin({
      root: this._resourcesService.rootFolder().pipe(take(1)),
      companyNodes: this._loadCompanyNodes(pinned),
    })
      .pipe(
        switchMap(({ root, companyNodes }) =>
          this._toFolderNodes(root.children).pipe(
            map((userChildren) => {
              this._userNode = {
                key: root.folderId,
                label: 'Eigene Ressourcen',
                icon: 'pi pi-user',
                leaf: userChildren.length === 0,
                children: userChildren,
                data: { kind: 'user-root' } satisfies MoveNodeData,
              };
              return this._buildModel(companyNodes);
            }),
          ),
        ),
        takeUntilDestroyed(this._destroyRef),
      )
      .subscribe({
        next: (nodes) => {
          this.treeNodes.set(nodes);
          this.loading.set(false);
          if (pinned) {
            this._expandPinnedCompany(nodes);
          }
        },
        error: () => {
          this.treeNodes.set([]);
          this.loading.set(false);
        },
      });
  }

  public onSearch(event: Event): void {
    this._search$.next((event.target as HTMLInputElement).value);
  }

  /** Reloads companies server-side for the (trimmed) query. */
  public applyFilter(query: string): void {
    const trimmed = query.trim();
    if (trimmed === this._query) {
      return;
    }
    this._query = trimmed;
    this._reloadCompanies(trimmed);
  }

  public onExpand(event: TreeNodeExpandEvent): void {
    const node = event.node;
    if (!node || (node.children?.length ?? 0) > 0) {
      return;
    }
    const kind = (node.data as MoveNodeData | undefined)?.kind;
    if (kind === 'company') {
      this._expandCompany(node);
    } else {
      // user-root, group and folder nodes are all backed by a folder id (the
      // node key); their subfolders are loaded on demand.
      this._expandFolder(node);
    }
  }

  public onSelect(event: TreeNodeSelectEvent): void {
    const node = event.node;
    const data = node?.data as MoveNodeData | undefined;
    if (data?.kind === 'more-companies') {
      // The "load more" row is an action, not a destination.
      this.selectedNode = null;
      this._loadMoreCompanies();
      return;
    }
    if (data?.forbidden) {
      // A locked group is not a valid destination.
      this.selectedNode = null;
      return;
    }
    this.folderChange.emit((node?.key as string) ?? null);
  }

  // --- company loading ------------------------------------------------------

  /**
   * The company context to scope the tree to, read as a synchronous snapshot:
   * this form only ever lives in a modal dialog, so the header selector cannot
   * change the context while it is open (unlike the permanently mounted scope
   * sidebar, which follows `selectedCompany$` live).
   *
   * Ignored when {@link includeOwnResources} is off. That flag marks the
   * administration screens, which act **on behalf of another user** — the
   * acting admin's own header context is not the relevant scope for somebody
   * else's resources, and applying it could hide the only valid destination.
   */
  private _readCompanyContext(): BdCompany | undefined {
    return this.includeOwnResources()
      ? this._companyContext.currentCompany
      : undefined;
  }

  /**
   * First page of destination companies — or, when a company context is
   * active, just that one company (no listing request, no "load more" row).
   */
  private _loadCompanyNodes(
    pinned: BdCompany | undefined,
  ): Observable<TreeNode[]> {
    if (pinned) {
      this._companyPage = 0;
      this._companiesLast = true;
      return of([this._companyNode(pinned)]);
    }
    return this._resourcesService
      .searchMyCompanies(0, BdMoveFormComponent.COMPANY_PAGE_SIZE)
      .pipe(
        take(1),
        map((page) => {
          this._companyPage = page.number;
          this._companiesLast = page.last;
          return page.content.map((c) => this._companyNode(c));
        }),
      );
  }

  /**
   * With a single company in view its groups are the only destinations worth
   * showing, so they are loaded and expanded right away — mirroring the scope
   * sidebar's single-company mode.
   */
  private _expandPinnedCompany(nodes: TreeNode[]): void {
    const companyNode = nodes.find(
      (n) => (n.data as MoveNodeData).kind === 'company',
    );
    if (companyNode) {
      this._expandCompany(companyNode);
    }
  }

  private _expandCompany(node: TreeNode): void {
    const companyId = (node.data as MoveNodeData).companyId;
    if (!companyId) {
      return;
    }
    node.loading = true;
    this._allGroupsOf(companyId)
      .pipe(
        take(1),
        switchMap((groups) => this._resolveGroupNodes(groups)),
        takeUntilDestroyed(this._destroyRef),
      )
      .subscribe({
        next: (children) => {
          node.loading = false;
          this.treeNodes.update((nodes) =>
            this._replaceNodeChildren(nodes, node.key as string, children),
          );
        },
        error: () => {
          // Group listing failed entirely — clear the spinner instead of
          // leaving the company row spinning forever.
          node.loading = false;
          this.treeNodes.update((nodes) => [...nodes]);
        },
      });
  }

  private _reloadCompanies(query: string): void {
    this.loading.set(true);
    this._resourcesService
      .searchMyCompanies(0, BdMoveFormComponent.COMPANY_PAGE_SIZE, query)
      .pipe(take(1), takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: (page) => {
          this._companyPage = page.number;
          this._companiesLast = page.last;
          this.treeNodes.set(
            this._buildModel(page.content.map((c) => this._companyNode(c))),
          );
          this.loading.set(false);
        },
        error: () => this.loading.set(false),
      });
  }

  private _loadMoreCompanies(): void {
    // The skeleton splice no longer blocks the row, so guard against a double
    // click paging in the same page twice (which would duplicate companies).
    if (this._companiesLast || this.loadingMore()) {
      return;
    }
    this.loadingMore.set(true);
    this._resourcesService
      .searchMyCompanies(
        this._companyPage + 1,
        BdMoveFormComponent.COMPANY_PAGE_SIZE,
        this._query,
      )
      .pipe(take(1), takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: (page) => {
          this._companyPage = page.number;
          this._companiesLast = page.last;
          this.treeNodes.update((nodes) => {
            const companies = nodes.filter(
              (n) => (n.data as MoveNodeData).kind === 'company',
            );
            return this._buildModel([
              ...companies,
              ...page.content.map((c) => this._companyNode(c)),
            ]);
          });
          this.loadingMore.set(false);
        },
        error: () => this.loadingMore.set(false),
      });
  }

  /** Walks every page of a single company's groups (bounded, on demand). */
  private _allGroupsOf(companyId: string): Observable<BdCompanyGroup[]> {
    const size = BdMoveFormComponent.GROUP_PAGE_SIZE;
    return this._resourcesService.groupsOfCompany(companyId, 0, size).pipe(
      expand((page) =>
        page.last ||
        page.content.length === 0 ||
        page.number + 1 >= page.totalPages
          ? EMPTY
          : this._resourcesService.groupsOfCompany(
              companyId,
              page.number + 1,
              size,
            ),
      ),
      reduce(
        (all: BdCompanyGroup[], page) => all.concat(page.content),
        [] as BdCompanyGroup[],
      ),
    );
  }

  /**
   * Resolves each group's root folder so the group node is selectable. A group
   * whose folder can't be resolved — most commonly a 403 because the user is
   * not a member — must not abort the whole listing: it degrades to a locked,
   * non-selectable node so the sibling groups still load.
   */
  private _resolveGroupNodes(groups: BdCompanyGroup[]): Observable<TreeNode[]> {
    if (groups.length === 0) {
      return of([]);
    }
    return forkJoin(
      groups.map((group) =>
        this._resourcesService.groupRootFolder(group.id).pipe(
          take(1),
          map((folder) => {
            const hasFolderChild = folder.children.some((c) => c.isFolder);
            return {
              key: folder.folderId,
              label: group.name,
              icon: 'pi pi-users',
              leaf: !hasFolderChild,
              children: [],
              data: { kind: 'group' } satisfies MoveNodeData,
            } as TreeNode;
          }),
          catchError((error: unknown) =>
            of(this._forbiddenGroupNode(group, error)),
          ),
        ),
      ),
    );
  }

  /**
   * Fallback node for a group whose root folder request failed. A 403 means the
   * user is not a member; anything else is an unexpected load failure. Either
   * way the group can't be a move target, so it is locked and non-selectable
   * with an explanatory tooltip.
   */
  private _forbiddenGroupNode(group: BdCompanyGroup, error: unknown): TreeNode {
    const forbidden =
      error instanceof HttpErrorResponse && error.status === 403;
    return {
      key: `group-locked:${group.id}`,
      label: group.name,
      // Keep the group's leading icon; the lock is appended after the name (in
      // grey) by the node template so it reads as "group, but locked".
      icon: 'pi pi-users',
      leaf: true,
      selectable: false,
      children: [],
      data: {
        kind: 'group',
        forbidden: true,
        tooltip: forbidden
          ? 'Kein Zugriff: Sie sind kein Mitglied dieser Gruppe.'
          : 'Diese Gruppe konnte nicht geladen werden.',
      } satisfies MoveNodeData,
    };
  }

  /**
   * Assembles the tree value. When not searching the user's own resources lead
   * the list; while searching only the matching companies remain (the fixed
   * user root isn't a search hit). A "load more" row trails when more company
   * pages exist.
   */
  private _buildModel(companyNodes: TreeNode[]): TreeNode[] {
    const more = this._companiesLast ? [] : [this._moreNode()];
    if (this._query !== '') {
      return [...companyNodes, ...more];
    }
    const userNode =
      this._userNode && this.includeOwnResources() ? [this._userNode] : [];
    return [...userNode, ...companyNodes, ...more];
  }

  private _companyNode(company: BdCompany): TreeNode {
    return {
      key: `company:${company.id}`,
      label: company.name,
      icon: 'pi pi-building',
      selectable: false,
      leaf: false,
      children: [],
      data: { kind: 'company', companyId: company.id } satisfies MoveNodeData,
    };
  }

  private _moreNode(): TreeNode {
    return {
      key: BdMoveFormComponent.MORE_COMPANIES_KEY,
      label: 'Weitere Kunden laden …',
      icon: 'pi pi-ellipsis-h',
      leaf: true,
      data: { kind: 'more-companies' } satisfies MoveNodeData,
    };
  }

  private _skeletonNode(index: number): TreeNode {
    return {
      key: `skeleton:${index}`,
      type: 'skeleton',
      label: '',
      leaf: true,
      selectable: false,
      styleClass: 'bd-move-form__skeleton-node',
      data: { kind: 'skeleton' } satisfies MoveNodeData,
    };
  }

  // --- folder loading (unchanged behaviour) ---------------------------------

  private _expandFolder(node: TreeNode): void {
    const targetKey = node.key as string | undefined;
    if (!targetKey) {
      return;
    }
    this._resourcesService
      .folder(targetKey)
      .pipe(take(1))
      .subscribe((folder) => {
        this._toFolderNodes(folder.children).subscribe((children) => {
          // PrimeNG's <p-tree-node> tracks by node identity, so we must replace
          // every ancestor reference along the path for the row to re-render.
          this.treeNodes.update((nodes) =>
            this._replaceNodeChildren(nodes, targetKey, children),
          );
        });
      });
  }

  private _replaceNodeChildren(
    nodes: TreeNode[],
    targetKey: string,
    children: TreeNode[],
  ): TreeNode[] {
    let changed = false;
    const next = nodes.map((node) => {
      if (node.key === targetKey) {
        changed = true;
        return { ...node, expanded: true, children };
      }
      if (node.children && node.children.length > 0) {
        const replaced = this._replaceNodeChildren(
          node.children,
          targetKey,
          children,
        );
        if (replaced !== node.children) {
          changed = true;
          return { ...node, children: replaced };
        }
      }
      return node;
    });
    return changed ? next : nodes;
  }

  private _toFolderNodes(
    children: ShareableFolderObj['children'],
  ): Observable<TreeNode[]> {
    const folderChildren = children.filter((c) => c.isFolder);
    if (folderChildren.length === 0) {
      return of([]);
    }
    return forkJoin(
      folderChildren.map((c) =>
        this._resourcesService.folder(c.shareableId).pipe(
          take(1),
          map((folder) => {
            const hasFolderChild = folder.children.some((cc) => cc.isFolder);
            return {
              key: c.shareableId,
              label: c.name || c.shareableId,
              icon: 'pi pi-folder',
              leaf: !hasFolderChild,
              children: [],
              data: { kind: 'folder' } satisfies MoveNodeData,
            } as TreeNode;
          }),
        ),
      ),
    );
  }
}
